import { Fragment, useEffect, useState, useCallback } from 'react';
import { apiFetch, getToken, STREAM_API } from '../utils/streamApi';
import PatrolLiveView from '../components/PatrolLiveView';
import MediaLightbox, { popupLink } from '../components/MediaLightbox';
import { groupRounds, roundLabel } from '../utils/shiftRounds';

// Guard Patrol report — history of patrol occurrences with visual proof, modelled
// on the Attendance report. Rows = one per checkpoint per occurrence, joined with
// the check-in event for evidence + identity. Responsive (opens on mobile).

const STATUS_STYLE = {
  VERIFIED:                 { label: 'Verified',        bg: 'rgba(80,180,120,0.18)',  fg: '#5cc98a' },
  COMPLETED:                { label: 'Checked in',      bg: 'rgba(80,180,120,0.14)',  fg: '#5cc98a' },
  VISITED_UNCONFIRMED:      { label: 'Unconfirmed',     bg: 'rgba(210,180,90,0.16)',  fg: '#d9be5e' },
  VISITED_DIFFERENT_PERSON: { label: 'Different person',bg: 'rgba(210,140,80,0.18)',  fg: '#e0a35e' },
  MISSED:                   { label: 'Missed',          bg: 'rgba(210,90,90,0.18)',   fg: '#e07070' },
  PENDING:                  { label: 'Pending',         bg: 'rgba(150,150,150,0.14)', fg: '#aaa' },
  // Window still open — not yet reached, as opposed to finalized-and-missed.
  AWAITING:                 { label: 'In progress',    bg: 'rgba(90,150,210,0.16)',  fg: '#6aa9de' },
};

// Statuses that mean the checkpoint is finished — nothing left to watch live.
const PATROL_DONE = new Set(['COMPLETED', 'VERIFIED', 'VISITED_UNCONFIRMED', 'VISITED_DIFFERENT_PERSON']);

const ymd = d => d.toISOString().slice(0, 10);
const toEpochStart = s => new Date(`${s}T00:00:00`).getTime() / 1000;
const toEpochEnd   = s => new Date(`${s}T23:59:59`).getTime() / 1000;
const fmt = ts => { try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; } };

export default function PatrolReportPage() {
  const [start, setStart]     = useState(ymd(new Date(Date.now() - 7 * 86400 * 1000)));
  const [end, setEnd]         = useState(ymd(new Date()));
  const [rows, setRows]       = useState([]);
  const [token, setToken]     = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState(null);   // live /ui/patrol/status
  const [watch, setWatch]     = useState(null);    // checkpoint being watched live, or null
  const [enrolling, setEnrolling] = useState(null); // event id mid-enrolment
  const [preview, setPreview] = useState(null);    // { kind, url, title } — evidence popup
  const [fRoute, setFRoute]       = useState('');  // history filters
  const [fStatus, setFStatus]     = useState('');
  const [fIdentity, setFIdentity] = useState('');

  const [scheds, setScheds]   = useState({});      // route id/name → schedule

  useEffect(() => { getToken().then(t => setToken(t || '')); }, []);

  // Route schedules drive the round boundaries in the history table. Read from
  // the config rather than /ui/patrol/status: status lists only active routes,
  // and history legitimately contains rounds from routes since deactivated.
  useEffect(() => {
    apiFetch('/ui/patrol/config').then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      const m = {};
      (d.routes || []).forEach(rt => { if (rt.id) m[rt.id] = rt.schedule; if (rt.name) m[rt.name] = rt.schedule; });
      setScheds(m);
    }).catch(() => {});
  }, []);

  // Live status poll — countdowns + live per-checkpoint state.
  useEffect(() => {
    let alive = true;
    const poll = () => apiFetch('/ui/patrol/status').then(r => r.ok ? r.json() : null).then(d => { if (alive && d) setStatus(d); }).catch(() => {});
    poll();
    const t = setInterval(poll, 6000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Stop watching once the checkpoint checks in. PatrolLiveView is a stateless
  // readout — it recomputes pose/zone/face on each poll and knows nothing of
  // engine state — so it sat on "WAITING…" after the check-in was already
  // recorded and had to be closed by hand. The chip beside it already flips to
  // CHECKED IN, so the live panel has nothing left to show.
  useEffect(() => {
    if (!watch) return;
    for (const rt of (status?.routes || [])) {
      for (const c of (rt.active || [])[0]?.checkpoints || []) {
        if (c.id === watch.id && PATROL_DONE.has(c.status)) { setWatch(null); return; }
      }
    }
  }, [watch, status]);

  // Enrol the person in an unconfirmed check-in's evidence photo as a known
  // face, and back-fill the record's identity. The evidence is a full frame, so
  // the server may legitimately find no usable face (guard distant or turned
  // away) — that comes back as 422 and is shown rather than swallowed.
  const addKnown = async (eventId) => {
    const name = window.prompt('Name for this person (added to known faces):');
    if (!name || !name.trim()) return;
    setEnrolling(eventId);
    try {
      const r = await apiFetch(`/ui/patrol/event/${eventId}/add-known`,
                               { method: 'POST', json: { name: name.trim() } });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.detail || `Failed (${r.status})`);
      }
      await load();          // pull the back-filled identity into the table
    } catch (e) {
      window.alert(e.message || 'Could not add this face.');
    } finally {
      setEnrolling(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/ui/patrol/report?start=${toEpochStart(start)}&end=${toEpochEnd(end)}`);
      const d = r.ok ? await r.json() : { occurrences: [], events: [] };
      const evMap = {};
      // merge records by checkpoint — the initial event + the later clip-ready update
      (d.events || []).forEach(e => {
        const k = `${e.occurrence_key}|${e.checkpoint_id}`;
        const prev = evMap[k];
        const merged = { ...prev, ...e };
        // The clip-ready update is appended after the encode under the same id and
        // carries its own ts. Left to the plain spread it overwrites the check-in
        // moment with the time the video finished writing — so keep the first ts,
        // which is the only record of when the guard actually checked in.
        if (prev?.ts && e._update) merged.ts = prev.ts;
        evMap[k] = merged;
      });
      const out = [];
      (d.occurrences || []).forEach(o => {
        Object.values(o.checkpoints || {}).forEach(cp => {
          const ev = evMap[`${o.key}|${cp.id}`];
          out.push({
            time: o.scheduled_time, route: o.route_name, routeId: o.route_id, checkpoint: cp.name,
            // When it actually happened. checked_in_ts is authoritative; records
            // written before it existed fall back to the event ts.
            checkinTs: cp.checked_in_ts ?? ev?.ts ?? null,
            status: cp.identity_status || cp.status,
            identName: cp.identified_person || ev?.identified_person,
            // The round is still running: a PENDING checkpoint here has not
            // been missed, the guard simply has not reached it yet.
            inProgress: !!o.in_progress,
            ev,
          });
        });
      });
      out.sort((a, b) => b.time - a.time);
      setRows(out);
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const evUrl = fname => `${STREAM_API}/ui/patrol/evidence/${encodeURIComponent(fname)}${token ? `?token=${token}` : ''}`;
  const evTitle = r => [r.route, r.checkpoint, fmt(r.time)].filter(Boolean).join(' · ');

  const routeOpts    = [...new Set(rows.map(r => r.route).filter(Boolean))].sort();
  const statusOpts   = [...new Set(rows.map(r => r.status).filter(Boolean))].sort();
  const identityOpts = [...new Set(rows.map(r => r.identName).filter(Boolean))].sort();
  const filtered = rows.filter(r =>
    (!fRoute    || r.route    === fRoute) &&
    (!fStatus   || r.status   === fStatus) &&
    (!fIdentity || r.identName === fIdentity));

  // One block per patrol round, cut on the route's own schedule start/end.
  const groups = groupRounds(filtered, r => r.time,
    r => ({ key: r.routeId || r.route, label: r.route, sched: scheds[r.routeId] || scheds[r.route] }),
    '22:00', '06:00');

  const counts = filtered.reduce((a, r) => { const k = r.status === 'MISSED' ? 'missed' : 'done'; a[k] = (a[k] || 0) + 1; return a; }, {});
  const statusLabel = k => (STATUS_STYLE[k]?.label || k);

  // Today's summary, computed from the loaded rows rather than a new endpoint —
  // the history already carries every checkpoint of every round. Uses the whole
  // unfiltered set so the tiles keep meaning "today on this site" even while the
  // table below is filtered down to one route.
  const todayStart = new Date(new Date().setHours(0, 0, 0, 0)).getTime() / 1000;
  const today = rows.filter(r => r.time >= todayStart);
  const todayCounts = {
    total:       today.length,
    checkedIn:   today.filter(r => PATROL_DONE.has(r.status)).length,
    missed:      today.filter(r => r.status === 'MISSED').length,
    unconfirmed: today.filter(r => r.status === 'VISITED_UNCONFIRMED' || r.status === 'VISITED_DIFFERENT_PERSON').length,
    inProgress:  today.filter(r => r.inProgress && r.status === 'PENDING').length,
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🗒️ Patrol Report</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Guard check-in history with photo/clip proof.</p>

      {todayCounts.total > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          {[['Rounds today', todayCounts.total, 'var(--text)'],
            ['Checked in', todayCounts.checkedIn, '#5cc98a'],
            ['Missed', todayCounts.missed, '#e07070'],
            ['Unconfirmed', todayCounts.unconfirmed, '#d9be5e'],
            ['In progress', todayCounts.inProgress, '#6aa9de']].map(([l, v, c]) => (
            <div key={l} style={{ ...liveCard, marginTop: 0, minWidth: 108, textAlign: 'center', padding: 14 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Live status: countdowns, grace window, live per-checkpoint state ── */}
      {status && (status.enabled === false
        ? <div style={{ ...liveCard, color: 'var(--text-muted)' }}>Guard Patrol is off — enable it on the Guard Patrol config page.</div>
        : (status.routes || []).length === 0
          ? <div style={{ ...liveCard, color: 'var(--text-muted)' }}>No active routes.</div>
          : (status.routes || []).map(rt => {
              const now = status.now, cur = rt.current_window, nxt = rt.next_window;
              const occ = (rt.active || [])[0];
              const liveByCp = {};
              (occ?.checkpoints || []).forEach(c => { liveByCp[c.id] = c; });
              return (
                <div key={rt.id} style={liveCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <b style={{ fontSize: 16 }}>{rt.name}</b>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {cur
                        ? <>🟢 Check-in window <b style={{ color: '#5cc98a' }}>OPEN</b> — {fmtClock(cur.window_start)}–{fmtClock(cur.window_end)} · closes in {fmtDur(cur.window_end - now)}</>
                        : nxt
                          ? <>Next patrol <b>{fmtClock(nxt.scheduled_time)}</b> — in {fmtDur(nxt.window_start - now)} · window {fmtClock(nxt.window_start)}–{fmtClock(nxt.window_end)} (grace before &amp; after)</>
                          : 'No upcoming patrol.'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {rt.checkpoints.map(cp => {
                      const st = liveByCp[cp.id]?.status;
                      const s = STATUS_STYLE[st] || null;
                      return (
                        <div key={cp.id} style={cpChip}>
                          <span>{cp.name}</span>
                          {st && s && <span style={{ ...badge, background: s.bg, color: s.fg }}>{s.label}</span>}
                          <button onClick={() => setWatch(w => w?.id === cp.id ? null : { ...cp, routeName: rt.name })} style={watchBtn}>
                            {watch?.id === cp.id ? '■ Stop' : '▶ Watch'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }))}

      {watch && (
        <div style={{ ...liveCard, background: '#0b0b0b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <b>Live — {watch.routeName} / {watch.name}</b>
            <button onClick={() => setWatch(null)} style={watchBtn}>Close</button>
          </div>
          <PatrolLiveView cameraIdx={watch.camera_idx} polygon={watch.patrol_zone || []}
                          expectedGuard={watch.expected_guard} holdSeconds={watch.gesture_hold_seconds || 2} />
        </div>
      )}

      <h2 style={{ fontSize: 18, margin: '24px 0 0' }}>History</h2>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', margin: '16px 0' }}>
        <div><div style={lbl}>From</div><input type="date" value={start} onChange={e => setStart(e.target.value)} style={input} /></div>
        <div><div style={lbl}>To</div><input type="date" value={end} onChange={e => setEnd(e.target.value)} style={input} /></div>
        <button onClick={load} style={btn}>{loading ? 'Loading…' : 'Refresh'}</button>
        <div><div style={lbl}>Route</div>
          <select value={fRoute} onChange={e => setFRoute(e.target.value)} style={input}>
            <option value="">All routes</option>{routeOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select></div>
        <div><div style={lbl}>Status</div>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={input}>
            <option value="">All statuses</option>{statusOpts.map(o => <option key={o} value={o}>{statusLabel(o)}</option>)}
          </select></div>
        <div><div style={lbl}>Identity</div>
          <select value={fIdentity} onChange={e => setFIdentity(e.target.value)} style={input}>
            <option value="">All identities</option>{identityOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select></div>
        {(fRoute || fStatus || fIdentity) &&
          <button onClick={() => { setFRoute(''); setFStatus(''); setFIdentity(''); }} style={btn}>Clear</button>}
        <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
          {filtered.length} record{filtered.length === 1 ? '' : 's'} · {counts.done || 0} checked in · {counts.missed || 0} missed
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 640 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', background: 'var(--surface)' }}>
              {['Scheduled', 'Checked In', 'Route', 'Checkpoint', 'Status', 'Identity', 'Evidence'].map(h =>
                <th key={h} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                {loading ? 'Loading…' : (rows.length ? 'No records match the filters.' : 'No patrol records in this range.')}</td></tr>
            )}
            {groups.map(g => {
              const missed = g.rows.filter(r => r.status === 'MISSED').length;
              return (
            <Fragment key={g.key}>
                <tr>
                  <td colSpan={7} style={roundCell}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <b>{g.owner}</b>
                      <span>{roundLabel(g)}</span>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                        {g.sched?.start || '22:00'}–{g.sched?.end || '06:00'}
                      </span>
                      <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontWeight: 400 }}>
                        {g.rows.length - missed} checked in · {missed} missed
                      </span>
                    </div>
                  </td>
                </tr>
                {g.rows.map((r, i) => {
              const s = (r.inProgress && r.status === 'PENDING')
                ? STATUS_STYLE.AWAITING
                : (STATUS_STYLE[r.status] || STATUS_STYLE.PENDING);
              return (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}>{fmt(r.time)}</td>
                  <td style={td}>{r.checkinTs
                    ? <>{fmtClock(r.checkinTs)}
                        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>{lateLabel(r.checkinTs, r.time)}</span>
                      </>
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={td}>{r.route}</td>
                  <td style={td}>{r.checkpoint}</td>
                  <td style={td}><span style={{ ...badge, background: s.bg, color: s.fg }}>{s.label}</span></td>
                  <td style={td}>
                    {r.identName || '—'}
                    {/* A gesture check-in by someone unrecognised is still valid,
                        but used to be a dead end — the evidence is a full frame
                        and nothing linked it to enrolment. Offer it here. */}
                    {!r.identName && r.ev?.id && r.ev?.evidence_image && (
                      <button type="button" style={{ ...popupLink(link), marginLeft: 8 }}
                        disabled={enrolling === r.ev.id}
                        onClick={() => addKnown(r.ev.id)}>
                        {enrolling === r.ev.id ? 'Adding…' : '+ Add as known'}
                      </button>
                    )}
                  </td>
                  <td style={td}>
                    {r.ev?.evidence_image && <button type="button" style={popupLink(link)}
                      onClick={() => setPreview({ kind: 'image', url: evUrl(r.ev.evidence_image), title: evTitle(r) })}>Photo</button>}
                    {r.ev?.evidence_video
                      ? <button type="button" style={{ ...popupLink(link), marginLeft: 10 }}
                          onClick={() => setPreview({ kind: 'video', url: evUrl(r.ev.evidence_video), title: evTitle(r) })}>Clip</button>
                      : r.ev?.clip_status === 'processing' && <span style={{ marginLeft: 10, color: 'var(--text-muted)' }}>Clip…</span>}
                    {!r.ev?.evidence_image && !r.ev?.evidence_video && r.ev?.clip_status !== 'processing' && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                </tr>
              );
                })}
            </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <MediaLightbox media={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

const fmtClock = ts => { try { return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };
const fmtDur = sec => { const m = Math.max(0, Math.round(sec / 60)); if (m < 60) return `${m}m`; return `${Math.floor(m / 60)}h ${m % 60}m`; };
// Offset of the actual check-in from the scheduled time. The point of the column is
// whether the round was walked on schedule, and a bare clock does not answer that
// without the reader subtracting two times in their head.
const lateLabel = (actual, sched) => {
  if (!actual || !sched) return '';
  const d = actual - sched;
  if (Math.abs(d) < 60) return 'on time';
  return d > 0 ? `+${fmtDur(d)} late` : `${fmtDur(-d)} early`;
};

const liveCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const cpChip   = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-alt)' };
const watchBtn = { padding: '4px 10px', background: 'transparent', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12 };
const lbl   = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };
const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn   = { padding: '8px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const td    = { padding: '10px 12px', whiteSpace: 'nowrap' };
const roundCell = { padding: '8px 12px', fontSize: 13, background: 'var(--surface)',
                    borderTop: '2px solid var(--border)', color: 'var(--text)' };
const badge = { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 };
const link  = { color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' };
